import axios from 'axios';
import { toast } from 'react-toastify';

export const getApiErrorMessage = (error, defaultMessage = 'An unexpected error occurred.') => {
  const data = error?.response?.data;
  if (!data) return error?.message || defaultMessage;
  if (typeof data === 'string') return data;

  // 1. Check field-specific validation errors (e.g. { errors: { email: ["..."] } })
  if (data.errors) {
    if (typeof data.errors === 'string') return data.errors;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map(e => (typeof e === 'object' ? JSON.stringify(e) : String(e))).join(', ');
    }
    if (typeof data.errors === 'object') {
      const entries = Object.entries(data.errors);
      if (entries.length > 0) {
        const messages = entries
          .map(([_, val]) => {
            if (Array.isArray(val)) return val.join(', ');
            if (typeof val === 'string') return val;
            if (typeof val === 'object' && val !== null) return JSON.stringify(val);
            return String(val);
          })
          .filter(Boolean);

        if (messages.length > 0) {
          return messages.map(m => (m.endsWith('.') ? m : `${m}.`)).join(' ');
        }
      }
    }
  }

  // 2. Check explicit message (e.g. { status: "error", message: "You are already enrolled..." })
  if (data.message && typeof data.message === 'string') {
    return data.message;
  }

  // 3. Check error string
  if (typeof data.error === 'string') {
    return data.error;
  }

  // 4. Check nested error object (e.g. { error: { message: "..." } })
  if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
    return data.error.message;
  }

  // 5. Check detail (e.g. { detail: "..." })
  if (data.detail && typeof data.detail === 'string') {
    return data.detail;
  }

  // 6. Check error.message on Axios error
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }

  return defaultMessage;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
//   withCredentials: true,
});
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {  
      const shouldToast = !error.config?.skipToast;

      switch (error.response.status) {
        case 401:
          localStorage.removeItem('authToken');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('accountType');
          
          // Determine redirect based on current path
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/admin')) {
            window.location.href = '/admin/login';
          } else if (currentPath.startsWith('/organization')) {
            window.location.href = '/organization/login';
          } else {
            window.location.href = '/login';
          }
          break;
        case 403:
          // Forbidden
          if (shouldToast) {
            toast.error(getApiErrorMessage(error, 'You do not have permission to perform this action.'));
          }
          break;
        case 404:
          // Not found
          break;
        case 422:
          // Validation error
          if (shouldToast) {
            toast.error(getApiErrorMessage(error, 'Validation error occurred.'));
          }
          break;
        case 500:
          // Server error
          if (shouldToast) {
            toast.error(getApiErrorMessage(error, 'A server error occurred. Please try again later.'));
          }
          break;
        default:
          if (shouldToast) {
            toast.error(getApiErrorMessage(error, 'An error occurred. Please try again later.'));
          }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
